import { useCallback, useRef, useState } from "react";
import { UploadCloud } from "lucide-react";
import { cn } from "@lib/utils";
import { enqueueUpload } from "~/lib/uploads";
import type { UploadSlot } from "~/lib/uploads";

interface UploadDropzoneProps {
  // MIME accept filter, e.g. "video/*". Wired for video first; the primitive
  // is generic so the rest of the uploads frontend can migrate onto it.
  accept?: string;
  multiple?: boolean;
  disabled?: boolean;
  className?: string;
  // Fires with the immediate placeholder slot for each accepted file, so the
  // caller can render a slot (pending → processing) without blocking.
  onEnqueued?: (slot: UploadSlot) => void;
  children?: React.ReactNode;
}

function accepts(file: File, accept?: string): boolean {
  if (!accept) return true;
  return accept.split(",").some((raw) => {
    const pattern = raw.trim();
    if (!pattern) return false;
    if (pattern.endsWith("/*")) {
      return file.type.startsWith(pattern.slice(0, -1));
    }
    if (pattern.startsWith(".")) {
      return file.name.toLowerCase().endsWith(pattern.toLowerCase());
    }
    return file.type === pattern;
  });
}

export function UploadDropzone({
  accept = "video/*",
  multiple = true,
  disabled = false,
  className,
  onEnqueued,
  children,
}: UploadDropzoneProps) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [dragging, setDragging] = useState(false);

  const handleFiles = useCallback(
    (files: FileList | null) => {
      if (!files) return;
      for (const file of Array.from(files)) {
        if (!accepts(file, accept)) continue;
        // One bad file (a duplicate, an oversize rejection, or any unexpected
        // add failure) must never abort the rest of a multi-file pick. enqueue
        // already swallows the throw and returns null in those cases; the
        // try/catch is belt-and-suspenders so a future throw can't kill the loop.
        try {
          const slot = enqueueUpload(file);
          if (slot) onEnqueued?.(slot);
        } catch {
          // enqueue surfaces the reason (error slot or toast) itself.
        }
        if (!multiple) break;
      }
    },
    [accept, multiple, onEnqueued]
  );

  const onDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      setDragging(false);
      if (disabled) return;
      handleFiles(e.dataTransfer.files);
    },
    [disabled, handleFiles]
  );

  return (
    <div
      data-testid="upload-dropzone"
      onDragOver={(e) => {
        e.preventDefault();
        if (!disabled) setDragging(true);
      }}
      onDragLeave={() => setDragging(false)}
      onDrop={onDrop}
      onClick={() => !disabled && inputRef.current?.click()}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => {
        if ((e.key === "Enter" || e.key === " ") && !disabled) inputRef.current?.click();
      }}
      className={cn(
        "flex cursor-pointer flex-col items-center justify-center gap-3 rounded-xl border-2 border-dashed p-8 text-center transition-colors",
        dragging ? "border-primary-500 bg-primary-500/5" : "border-neutral-300 bg-neutral-background",
        disabled && "pointer-events-none opacity-50",
        className
      )}
    >
      <input
        ref={inputRef}
        type="file"
        accept={accept}
        multiple={multiple}
        className="hidden"
        data-testid="upload-dropzone-input"
        onChange={(e) => {
          handleFiles(e.target.files);
          e.target.value = "";
        }}
      />
      {children ?? (
        <>
          <UploadCloud className="size-8 text-neutral-500" />
          <div className="text-sm text-neutral-700">
            <span className="font-medium text-base-500">Click to upload</span> or drag and drop
          </div>
          <div className="text-xs text-neutral-500">
            Large files upload in the background — you can keep working.
          </div>
        </>
      )}
    </div>
  );
}
